import { DotsFilledIcon } from '@approvalmax/ui';
import { Button, Dropdown, Menu, Popup, toast } from '@approvalmax/ui/src/components';
import { useAtom } from 'jotai';
import { memo, useCallback } from 'react';
import { useToggle } from 'react-use';
import { useSyncQBooksBudget, useUpdateQBooksBudget } from 'shared/data/webApp/v2';

import { syncQBooksBudgetsPageState } from '../../../../states';
import { BudgetSettingsPopupContent } from '../BudgetSettingsPopupContent/BudgetSettingsPopupContent';
import { messages } from './ActionsMenu.messages';
import { ActionsMenuProps } from './ActionsMenu.types';

export const ActionsMenu = memo<ActionsMenuProps>((props) => {
    const { budget, company, isBudgetSyncEnabled, onSyncStart, onSyncEnd } = props;

    const { budgetId, isEnabled } = budget;

    const [syncQBooksBudgetsState, setSyncQBooksBudgetsState] = useAtom(syncQBooksBudgetsPageState);

    const { mutate: updateQBooksBudget } = useUpdateQBooksBudget({
        onSuccess: (data) => {
            toast.success(data.isEnabled ? messages.hasBeenActivated : messages.hasBeenDeactivated);
        },
    });

    const { mutate: syncQBooksBudget } = useSyncQBooksBudget({
        onSuccess: () => {
            setSyncQBooksBudgetsState(true);
        },
        onSettled: onSyncEnd,
    });

    const [isSettingsPopupOpen, toggleSettingsPopupOpen] = useToggle(false);

    const {
        isReadonly,
        flags: { hasActiveIntegration, isAuditor },
    } = company;

    const handleToggleBudgetIsEnabled = useCallback(() => {
        updateQBooksBudget({
            params: {
                path: {
                    companyId: company.id,
                    budgetId,
                },
            },
            body: { isEnabled: !isEnabled },
        });
    }, [updateQBooksBudget, company.id, budgetId, isEnabled]);

    const handleShowSettingsPopup = useCallback(() => {
        toggleSettingsPopupOpen(true);
    }, [toggleSettingsPopupOpen]);

    const handleCloseSettingsPopup = useCallback(() => {
        toggleSettingsPopupOpen(false);
    }, [toggleSettingsPopupOpen]);

    const handleBudgetRefresh = useCallback(() => {
        onSyncStart();

        syncQBooksBudget({
            params: {
                path: { companyId: company.id, budgetId: budget.budgetId },
            },
        });
    }, [onSyncStart, syncQBooksBudget, company.id, budget.budgetId]);

    return (
        <>
            <Dropdown
                width='max-content'
                closeOnClickInnerButton
                activator={
                    <Button icon noPadding variant='text' color='blue100'>
                        <DotsFilledIcon size={24} />
                    </Button>
                }
            >
                <Menu size='small'>
                    <Menu.Item
                        name={isEnabled ? messages.deactivateMenuName : messages.activateMenuName}
                        title={isEnabled ? messages.deactivateMenuTitle : messages.activateMenuTitle}
                        onClick={handleToggleBudgetIsEnabled}
                        disabled={isReadonly}
                    />

                    <Menu.Item
                        name={messages.showSettingsMenuName}
                        title={messages.showSettingsMenuTitle}
                        onClick={handleShowSettingsPopup}
                        disabled={isAuditor || isReadonly}
                    />

                    <Menu.Item
                        name={messages.refreshMenuName}
                        title={!isBudgetSyncEnabled ? messages.syncIsDisabled : messages.refreshMenuTitle}
                        onClick={handleBudgetRefresh}
                        disabled={
                            !hasActiveIntegration ||
                            !isEnabled ||
                            isReadonly ||
                            syncQBooksBudgetsState ||
                            !isBudgetSyncEnabled
                        }
                    />
                </Menu>
            </Dropdown>

            <Popup open={isSettingsPopupOpen} onToggle={toggleSettingsPopupOpen}>
                <BudgetSettingsPopupContent budget={budget} onClosePopup={handleCloseSettingsPopup} />
            </Popup>
        </>
    );
});

ActionsMenu.displayName = 'ActionsMenu';
